import React, { useState, useEffect, useRef } from 'react';
import { Divider, Row, Col, Card, Descriptions, Typography, Tabs, Space } from "antd";
import SurfacePlot3d from "./SurfacePlot3d";
import Simulations3dSurface from "./Simulations3dSurface";
import SimulationView from "./SimulationView";
import ReusableHeatmap from "./ReusableHeatmap";
import OneToMany from "./OneToMany";
import TailMatrixChart from "./TailMatrixChart";
import Dot from "../../../components/Icons/Dot";
import {Circle} from 'lucide-react'

const CopulaView = ({ data }) => {
    const { info, heat_matrix, simulations, surface_vectors, tail_matrix } = data;
    const [activeTab, setActiveTab] = useState('1');
    const tabContentRef = useRef(null);
    const [isScrolling, setIsScrolling] = useState(false);

    const handleScroll = () => {
        if (isScrolling || !tabContentRef.current) return;

        const scrollPosition = window.scrollY + window.innerHeight;
        const contentHeight = tabContentRef.current.offsetTop + tabContentRef.current.offsetHeight;
        const scrollPercentage = (scrollPosition / contentHeight) * 100;

        if (scrollPercentage > 85 && activeTab !== '3') {
            setIsScrolling(true);
            setActiveTab(prevTab => {
                const nextTab = String(Number(prevTab) + 1);
                return nextTab <= '3' ? nextTab : prevTab;
            });
            setTimeout(() => setIsScrolling(false), 500);
        } else if (scrollPosition < window.innerHeight && activeTab !== '1') {
            setIsScrolling(true);
            setActiveTab('1');
            setTimeout(() => setIsScrolling(false), 500);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeTab, isScrolling]);

    const renderDetailsTable = () => {
        const data = [
            { key: 1, property: 'Model Type', value: "Elliptical Copula"},
            { key: 2, property: 'Copula Type', value: info.copula_type },
            { key: 3, property: 'Start Date', value: info.start_date },
            { key: 4, property: 'End Date', value: info.end_date },
            { key: 5, property: '# Assets', value: info.n_assets },
        ];

        return (
            <Descriptions column={data.length} layout={'vertical'} colon={''}>
                {data.map((item) => (
                    <Descriptions.Item label={item.property} key={item.key}>
                        {item.value}
                    </Descriptions.Item>
                ))}
            </Descriptions>
        );
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case '1':
                return (
                    <>
                        <Row gutter={[16, 16]} justify={'center'}>
                            <Col span={16}>
                                <Card bordered={false}>
                                    {renderDetailsTable()}
                                </Card>
                            </Col>
                        </Row>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} xl={12} style={{textAlign: 'center'}}>
                                <ReusableHeatmap
                                    matrix={heat_matrix}
                                    colorscale={'Plasma'}
                                    title={'Copula Correlation'}
                                    reverse={true}
                                />
                            </Col>
                            <Col xs={24} xl={12} style={{textAlign: 'center'}}>
                                <OneToMany data={heat_matrix}
                                           titlePrefix={'Copula Correlation'}
                                />
                            </Col>
                        </Row>
                        <Divider/>
                        <Row>
                            <Col span={24} className={'py-6 pr-6'}>
                                <Tabs tabPosition={'left'} defaultActiveKey={'0'}>
                                    <Tabs.TabPane tab={'Grid'} key={'0'}>
                                        <Card className={'gapless'} bordered={false}>
                                            <Row align={'middle'} justify={'start'}>
                                                <Typography.Title level={3}>Dependence During Tail Risk</Typography.Title>
                                                <Space className={'mx-4'}>
                                                    <Dot size={16} color={'#98bee1'} label={'Low Tail Dependence'} Icon={Circle} fillIcon={true} />
                                                    <Dot size={20} color={'#1f77b4'} label={'Moderate Tail Dependence'} Icon={Circle} fillIcon={true} />
                                                    <Dot size={24} color={'#FF1493'} label={'Extreme Tail Dependence'} Icon={Circle} fillIcon={true} />

                                                </Space>

                                            </Row>
                                            <TailMatrixChart data={tail_matrix} />
                                        </Card>
                                    </Tabs.TabPane>
                                    <Tabs.TabPane tab={'Heatmap'} key={'1'}>
                                        <Row>
                                            <Col  xs={24} xl={12} style={{textAlign: 'center'}}>
                                                <OneToMany data={tail_matrix} titlePrefix={'Tail Dependence'}
                                                           color={'rgba(223, 61, 118, 0.5)'}
                                                           showCircles={false}
                                                />
                                            </Col>
                                            <Col xs={24} xl={12} style={{textAlign: 'center'}}>
                                                <ReusableHeatmap
                                                    matrix={tail_matrix}
                                                    colorscale={'Cividis'}
                                                    title={'Tail Dependence'}

                                                />
                                            </Col>
                                        </Row>
                                    </Tabs.TabPane>
                                </Tabs>
                            </Col>
                            <Col span={24}>
                                <Col span={24}>
                                </Col>

                            </Col>
                        </Row>
                    </>
                );
            case '2':
                return (
                    <div className="min-h-screen">
                        <SimulationView
                            simulations={simulations}
                            assetNames={info.asset_names}
                        />
                    </div>
                );
            case '3':
                return (
                    <div className="min-h-screen bg-gradient-to-b from-black to-slate-900">
                        <Tabs defaultActiveKey="1" centered={true}>
                            <Tabs.TabPane tab={<Typography.Text style={{color: 'white'}}>Surface Plot</Typography.Text>} key="1">
                                <div className={'w-4/5 h-4/5 mx-auto'}>
                                    <SurfacePlot3d
                                        surfaceVectors={surface_vectors}
                                        labels={info.asset_names}
                                    />
                                </div>
                            </Tabs.TabPane>
                            <Tabs.TabPane tab={<Typography.Text style={{color: 'white'}}>Simulations</Typography.Text>}
                                          key="2">
                                <div className={'w-4/5 h-4/5 mx-auto'}>

                                    <Simulations3dSurface
                                        simulations={simulations}
                                        assetNames={info.asset_names}
                                    />
                                </div>
                            </Tabs.TabPane>
                        </Tabs>
                    </div>
            );
            default:
                return null;
        }
    };

    return (
        <div>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                centered={true}
                className="sticky top-0 bg-white z-50 shadow-sm"
            >
                <Tabs.TabPane tab="Overview" key="1" />
                <Tabs.TabPane tab="Simulations" key="2" />
                <Tabs.TabPane tab="3D Surfaces" key="3" />
            </Tabs>

            <div
                ref={tabContentRef}
                className="transition-opacity duration-300 ease-in-out"
                style={{ opacity: isScrolling ? 0.5 : 1 }}
            >
                {renderTabContent()}
            </div>
        </div>
    );
};

export default CopulaView;